import React, { useEffect, useState, useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
 Filter,
 ChevronRight,
 RefreshCw,
 CheckCircle,
 Clock,
 XCircle,
 Calendar,
 User,
 Car,
 DollarSign,
 Eye,
} from "lucide-react"
import {
 Card,
 CardContent,
 CardHeader,
 CardTitle,
 ModernButton,
 ModernSelect,
 Toast,
 ToastType,
 ListPageSkeleton,
 Table,
 TableHeader,
 TableBody,
 TableRow,
 TableHead,
 TableCell,
 ShadcnButton,
} from "../../components/ui"
import { DatePicker } from "../../components/form/DatePicker"
import { apiService } from "../../services/api"
import type { Sale, SaleOperationalStage } from "../../types"
import { formatCurrency, formatDateToBR } from "../../utils/formatters"
import { cn } from "@/lib/utils"

type OperationSaleListPageProps = {
  operationalStage?: SaleOperationalStage
  titleKey: string
  subtitleKey: string
  progressStageKey: string
  showAdvance?: boolean
  extraColumns?: (sale: Sale) => React.ReactNode
  getSaleDetailPath?: (saleId: string) => string
  statusFilter?: FilterStatus
}

type FilterStatus =
 | "all"
 | "open"
 | "pending"
 | "waiting_payment"
 | "paid"
 | "in_preparation"
 | "ready_for_delivery"
 | "delivered"
 | "confirmed"
 | "cancelled"

const NEXT_STAGE: Partial<Record<SaleOperationalStage, SaleOperationalStage>> = {
  pre_sale: "preparation",
  preparation: "delivery",
  delivery: "post_receiving",
  post_receiving: "completed",
}

const PAGE_SIZE = 20

function localDayStartMs(yyyyMmDd: string): number {
 const [y, m, d] = yyyyMmDd.split("-").map(Number)
 return new Date(y, m - 1, d, 0, 0, 0, 0).getTime()
}

function localDayEndMs(yyyyMmDd: string): number {
 const [y, m, d] = yyyyMmDd.split("-").map(Number)
 return new Date(y, m - 1, d, 23, 59, 59, 999).getTime()
}

function saleInstantMs(saleDate: string): number {
 return new Date(saleDate).getTime()
}

export const OperationSaleListPage: React.FC<OperationSaleListPageProps> = ({
  operationalStage,
  titleKey,
  subtitleKey,
  progressStageKey,
  showAdvance = true,
  extraColumns,
  getSaleDetailPath = (saleId: string) => `/financial/sales/${saleId}`,
  statusFilter,
}) => {
 const { t } = useTranslation()
 const navigate = useNavigate()
 const [sales, setSales] = useState<Sale[]>([])
 const [loading, setLoading] = useState(true)
 const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null)
 const [listPage, setListPage] = useState(1)

 const getFirstDayOfMonth = () => {
 const now = new Date()
 return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0]
 }

 const getToday = () => new Date().toISOString().split("T")[0]

 const [filters, setFilters] = useState<{
 status: FilterStatus
 startDate: string
 endDate: string
 }>({
 status: "all",
 startDate: getFirstDayOfMonth(),
 endDate: getToday(),
 })

const load = useCallback(async () => {
  try {
   setLoading(true)
   const res = await apiService.getSales({ 
     operationalStage, 
     status: statusFilter !== 'all' ? statusFilter : undefined,
     limit: 200, 
     page: 1 
   })
   setSales(Array.isArray(res.sales) ? res.sales : [])
  } catch {
   setToast({ message: t("operation.loadError"), type: "error" })
   setSales([])
  } finally {
   setLoading(false)
  }
 }, [operationalStage, statusFilter, t])

 useEffect(() => {
 void load()
 }, [load])

 useEffect(() => {
 setListPage(1)
 }, [filters])

 const progressRatio = (sale: Sale) => {
 const p = sale.operationalProgress?.[progressStageKey]
 if (!p || p.total === 0) return null
 return Math.round((p.done / p.total) * 100)
 }

 const handleAdvance = async (sale: Sale, e: React.MouseEvent) => {
 e.stopPropagation()
 const next = NEXT_STAGE[(sale.operationalStage ?? operationalStage) as keyof typeof NEXT_STAGE]
 if (!next) return
 try {
 await apiService.patchSaleOperationalStage(sale.id, next)
 setToast({ message: t("operation.advanceOk"), type: "success" })
 void load()
 } catch {
 setToast({ message: t("operation.advanceError"), type: "error" })
 }
 }

 const getStatusIcon = (status: Sale["status"]) => {
 switch (status) {
 case "confirmed":
 case "paid":
 case "delivered":
 return <CheckCircle className="h-5 w-5 text-green-600" />
 case "pending":
 case "open":
 case "waiting_payment":
 return <Clock className="h-5 w-5 text-yellow-600" />
 case "in_preparation":
 case "ready_for_delivery":
 return <Clock className="h-5 w-5 text-blue-600" />
 case "cancelled":
 return <XCircle className="h-5 w-5 text-red-600" />
 default:
 return <Clock className="h-5 w-5 text-muted-foreground" />
 }
 }

 const getStatusLabel = (status: Sale["status"]) =>
 t(`financialSales.statusLabels.${status}`, { defaultValue: status })

 const filteredSales = useMemo(() => {
 let list = sales
 if (filters.status !== "all") {
 list = list.filter((s) => s.status === filters.status)
 }
 if (filters.startDate) {
 const startMs = localDayStartMs(filters.startDate)
 list = list.filter((s) => saleInstantMs(s.saleDate) >= startMs)
 }
 if (filters.endDate) {
 const endMs = localDayEndMs(filters.endDate)
 list = list.filter((s) => saleInstantMs(s.saleDate) <= endMs)
 }
 return list
 }, [sales, filters])

 const totalListPages = Math.max(1, Math.ceil(filteredSales.length / PAGE_SIZE))
 const safeListPage = Math.min(listPage, totalListPages)
 const pagedSales = useMemo(() => {
 const start = (safeListPage - 1) * PAGE_SIZE
 return filteredSales.slice(start, start + PAGE_SIZE)
 }, [filteredSales, safeListPage])

 if (loading && sales.length === 0) {
 return (
 <div className="space-y-6">
 <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
 <div>
 <h1 className="text-2xl font-bold font-display text-foreground">{t(titleKey)}</h1>
 <p className="text-sm text-muted-foreground mt-1">{t(subtitleKey)}</p>
 </div>
 </div>
 <ListPageSkeleton rows={6} showFilters />
 </div>
 )
 }

 return (
 <div className="space-y-6">
 {toast && (
 <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
 )}
 <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
 <div>
 <h1 className="text-2xl font-bold font-display text-foreground">{t(titleKey)}</h1>
 <p className="text-sm text-muted-foreground mt-1">{t(subtitleKey)}</p>
 </div>
 <ModernButton
 type="button"
 variant="outline"
 size="sm"
 disabled={loading}
 onClick={() => void load()}
 >
 <RefreshCw className={cn("h-4 w-4 mr-2", loading && "animate-spin")} />
 {t("operation.refresh")}
 </ModernButton>
 </div>

 <Card>
 <CardHeader>
 <CardTitle className="flex items-center text-base">
 <Filter className="h-5 w-5 mr-2" />
 {t("financialSales.filters")}
 </CardTitle>
 </CardHeader>
 <CardContent>
 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
 <ModernSelect
 label={t("financialSales.status")}
 value={filters.status}
 onChange={(e) =>
 setFilters({
 ...filters,
 status: e.target.value as FilterStatus,
 })
 }
 options={[
 { value: "all", label: t("financialSales.statusAll") },
 { value: "open", label: t("financialSales.statusLabels.open") },
 { value: "pending", label: t("financialSales.statusLabels.pending") },
 { value: "waiting_payment", label: t("financialSales.statusLabels.waiting_payment") },
 { value: "paid", label: t("financialSales.statusLabels.paid") },
 { value: "in_preparation", label: t("financialSales.statusLabels.in_preparation") },
 {
 value: "ready_for_delivery",
 label: t("financialSales.statusLabels.ready_for_delivery"),
 },
 { value: "delivered", label: t("financialSales.statusLabels.delivered") },
 { value: "confirmed", label: t("financialSales.statusLabels.confirmed") },
 { value: "cancelled", label: t("financialSales.statusLabels.cancelled") },
 ]}
 placeholder=""
 />
 <div>
 <DatePicker
 label={t("financialSales.startDate")}
 value={filters.startDate}
 onChange={(value) => setFilters({ ...filters, startDate: value })}
 placeholder={t("financialSales.startDatePlaceholder")}
 />
 </div>
 <div>
 <DatePicker
 label={t("financialSales.endDate")}
 value={filters.endDate}
 onChange={(value) => setFilters({ ...filters, endDate: value })}
 placeholder={t("financialSales.endDatePlaceholder")}
 />
 </div>
 <div className="flex items-end">
 <ModernButton
 variant="outline"
 type="button"
 className="w-full"
 onClick={() =>
 setFilters({
 status: "all",
 startDate: "",
 endDate: "",
 })
 }
 >
 {t("financialSales.clearFilters")}
 </ModernButton>
 </div>
 </div>
 </CardContent>
 </Card>

 {filteredSales.length === 0 ? (
 <div className="text-center py-12 rounded-lg border border-border bg-card/30">
 <p className="text-muted-foreground">{t("operation.empty")}</p>
 </div>
 ) : (
 <div className="space-y-4">
 <div className="overflow-x-auto rounded-lg border border-border">
 <Table>
 <TableHeader>
 <TableRow>
 <TableHead>{t("operation.list.date")}</TableHead>
 <TableHead>{t("operation.list.customer")}</TableHead>
 <TableHead>{t("operation.list.vehicle")}</TableHead>
 <TableHead>{t("operation.list.finalValue")}</TableHead>
 <TableHead>{t("common.status")}</TableHead>
 <TableHead>{t("operation.list.progress")}</TableHead>
 {extraColumns ? <TableHead>{t("operation.list.details")}</TableHead> : null}
 <TableHead className="text-right">{t("operation.list.actions")}</TableHead>
 </TableRow>
 </TableHeader>
 <TableBody>
 {pagedSales.map((sale) => {
 const pct = progressRatio(sale)
 const cust = sale.customer?.name ?? "—"
 const veh = sale.vehicle
 const vehLabel = veh
 ? `${veh.brand?.name ?? ""} ${veh.model?.name ?? ""} ${veh.modelYear ?? ""}`.trim() || "—"
 : "—"
 const finalValue = parseFloat(String(sale.finalValue))
 const canAdvance = Boolean(
 showAdvance && NEXT_STAGE[(sale.operationalStage ?? operationalStage) as keyof typeof NEXT_STAGE]
 )
 return (
 <TableRow
 key={sale.id}
 className={cn("cursor-pointer hover:bg-muted/60")}
 role="button"
 tabIndex={0}
 onClick={() => navigate(getSaleDetailPath(sale.id))}
 onKeyDown={(e) => {
 if (e.key === "Enter" || e.key === " ") {
 e.preventDefault()
 navigate(getSaleDetailPath(sale.id))
 }
 }}
 >
 <TableCell>
 <div className="flex items-center gap-2">
 <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
 <span>{formatDateToBR(sale.saleDate)}</span>
 </div>
 </TableCell>
 <TableCell>
 <div className="flex items-center gap-2 min-w-0">
 <User className="h-4 w-4 text-muted-foreground shrink-0" />
 <span className="truncate">{cust}</span>
 </div>
 </TableCell>
 <TableCell>
 <div className="flex items-center gap-2 min-w-0">
 <Car className="h-4 w-4 text-muted-foreground shrink-0" />
 <span className="truncate max-w-[220px]">{vehLabel}</span>
 </div>
 </TableCell>
 <TableCell>
 <div className="flex items-center gap-2">
 <DollarSign className="h-4 w-4 text-green-600 shrink-0" />
 <span className="font-semibold whitespace-nowrap">{formatCurrency(finalValue)}</span>
 </div>
 </TableCell>
 <TableCell>
 <div className="flex items-center gap-2">
 {getStatusIcon(sale.status)}
 <span className="text-sm">{getStatusLabel(sale.status)}</span>
 </div>
 </TableCell>
 <TableCell>
 {pct !== null ? (
 <div className="w-[140px]">
 <div className="h-2 rounded-full bg-muted overflow-hidden">
 <div
 className="h-full rounded-full bg-accent transition-all"
 style={{ width: `${pct}%` }}
 />
 </div>
 <p className="text-[10px] text-muted-foreground mt-1">
 {t("operation.checklistProgress", { pct })}
 </p>
 </div>
 ) : (
 <span className="text-sm text-muted-foreground">—</span>
 )}
 </TableCell>
 {extraColumns ? <TableCell className="align-top">{extraColumns(sale)}</TableCell> : null}
 <TableCell className="text-right">
 <div className="flex flex-wrap justify-end gap-1">
 <ShadcnButton
 variant="ghost"
 size="sm"
 type="button"
 onClick={(e) => {
 e.stopPropagation()
 navigate(getSaleDetailPath(sale.id))
 }}
 >
 <Eye className="h-4 w-4 mr-1" />
 {t("actions.view")}
 </ShadcnButton>
 {canAdvance ? (
 <ShadcnButton
 variant="secondary"
 size="sm"
 type="button"
 onClick={(e) => void handleAdvance(sale, e)}
 >
 {t("operation.advance")}
 <ChevronRight className="h-4 w-4 ml-1" />
 </ShadcnButton>
 ) : null}
 </div>
 </TableCell>
 </TableRow>
 )
 })}
 </TableBody>
 </Table>
 </div>

 {totalListPages > 1 ? (
 <div className="flex items-center justify-between gap-4">
 <p className="text-sm text-muted-foreground">
 {t("operation.list.pageOf", { page: safeListPage, total: totalListPages })}
 </p>
 <div className="flex gap-2">
 <ShadcnButton
 variant="outline"
 size="sm"
 type="button"
 onClick={() => setListPage((p) => Math.max(1, p - 1))}
 disabled={safeListPage <= 1}
 >
 {t("actions.previous")}
 </ShadcnButton>
 <ShadcnButton
 variant="outline"
 size="sm"
 type="button"
 onClick={() => setListPage((p) => Math.min(totalListPages, p + 1))}
 disabled={safeListPage >= totalListPages}
 >
 {t("actions.next")}
 </ShadcnButton>
 </div>
 </div>
 ) : null}
 </div>
 )}
 </div>
 )
}
